import React, { useState, useEffect } from 'react'
import Card from 'core-system/Card'
import {
  ArrowUp,
  ArrowDown,
  Star,
  Mail,
  MessageSquare,
  UserCheck,
  BookOpen,
  Link as LinkIcon,
  Plus,
} from 'lucide-react'
import CountUp from 'react-countup'
import * as Tooltip from '@radix-ui/react-tooltip'
import {
  PieChart as RechartsPieChart,
  Pie as RechartsPie,
  Cell as RechartsCell,
  ResponsiveContainer as RechartsResponsiveContainer,
  BarChart as RechartsBarChart,
  Bar as RechartsBar,
  XAxis as RechartsXAxis,
  YAxis as RechartsYAxis,
  CartesianGrid as RechartsCartesianGrid,
  Legend as RechartsLegend,
  Tooltip as RechartsTooltip,
} from 'recharts'
import styled, { css } from 'styled-components'

// Simple className utility function
const cn = (...classes: (string | boolean | undefined)[]): string => {
  return classes.filter(Boolean).join(' ')
}

const MainContent = styled.main`
  flex: 1;
  overflow: auto;
`

const ContentContainer = styled.div`
  padding: 2rem;
`

const TitleSection = styled.div`
  margin-bottom: 2rem;
`

const PageTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`

const PageDescription = styled.p`
  font-size: 1rem;
  color: #4b5563;
`

const GridContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;
`

const KPICard = styled(Card)<{ $delay?: string }>`
  padding: 1.5rem;
  background-color: white;
  transition: all 0.2s;
  cursor: pointer;
  flex: 1 1 200px;
  min-width: 200px;
  max-width: 100%;

  &:hover {
    transform: scale(1.02);
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
    background-color: #f9fafb;
  }
`

const KPILabel = styled.div`
  font-size: 0.875rem;
  color: #4b5563;
  margin-bottom: 0.5rem;
`

const KPIValueContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`

const KPIValue = styled.span`
  font-size: 2.25rem;
  font-weight: 700;
  color: #111827;
`

const KPIUnit = styled.span`
  font-size: 1.125rem;
  color: #4b5563;
`

const ChangeIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: #16a34a;
`

const SkeletonBar = styled.div<{
  $width: string
  $height: string
  $marginBottom?: string
}>`
  height: ${(props) => props.$height};
  background-color: #e5e7eb;
  border-radius: 0.25rem;
  width: ${(props) => props.$width};
  margin-bottom: ${(props) => props.$marginBottom || '0'};
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`

const SkeletonCardContent = styled.div`
  padding: 1.5rem;
`

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ChartCard = styled(Card)`
  padding: 1.5rem;
`

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`

const ChartTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
`

const FilterButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`

const FilterButton = styled.button<{ $active: boolean }>`
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  border-radius: 9999px;
  transition: all 0.2s;
  border: none;
  cursor: pointer;

  ${(props) =>
    props.$active
      ? css`
          background-color: #a64d79;
          color: white;
        `
      : css`
          background-color: #f3f4f6;
          color: #4b5563;

          &:hover {
            background-color: #e5e7eb;
          }
        `}
`

const PieChartGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
`

const PieChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const PieChartWrapper = styled.div`
  position: relative;
  width: 12rem;
  height: 12rem;
`

const PieChartCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PieChartCenterContent = styled.div`
  text-align: center;
`

const PieChartValue = styled.div`
  font-size: 1.875rem;
  font-weight: 700;
  color: #111827;
`

const PieChartLabel = styled.div`
  font-size: 0.75rem;
  color: #4b5563;
`

const ChannelList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.75rem;
`

const ChannelItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: help;
  padding: 0.5rem;
  border-radius: 0.25rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f9fafb;
  }
`

const ChannelInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const ChannelIconContainer = styled.div<{ $color: string }>`
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => `${props.$color}20`};
`

const ChannelDetails = styled.div``

const ChannelName = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
`

const ChannelRequests = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`

const ChannelStats = styled.div`
  text-align: right;
`

const ChannelPercentage = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  color: #111827;
`

const ChannelTrend = styled.div<{ $positive: boolean }>`
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.125rem;
  color: ${(props) => (props.$positive ? '#16a34a' : '#dc2626')};
`

const TooltipContent = styled(Tooltip.Content)`
  background-color: #111827;
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  max-width: 20rem;
`

const TooltipArrow = styled(Tooltip.Arrow)`
  fill: #111827;
`

const BarChartContainer = styled.div`
  height: 16rem;
`

const BarChartFooter = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 1rem;
  text-align: center;
`

const ConfigCard = styled(Card)`
  padding: 1.5rem;
  margin-bottom: 2rem;
`

const ConfigTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1.5rem;
`

const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const SiteCard = styled(Card)<{ $disabled?: boolean }>`
  padding: 1.5rem;
  transition: all 0.2s;

  &:hover {
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  ${(props) =>
    props.$disabled &&
    css`
      opacity: 0.6;
    `}
`

const SiteHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`

const SiteName = styled.h4`
  font-weight: 600;
  color: #111827;
  font-size: 1.125rem;
`

const StatusBadge = styled.div<{ $active: boolean }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;

  ${(props) =>
    props.$active
      ? css`
          background-color: #dcfce7;
          color: #15803d;
        `
      : css`
          background-color: #f3f4f6;
          color: #4b5563;
        `}
`

const ServicesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`

const ServiceTag = styled.span`
  padding: 0.25rem 0.75rem;
  background-color: #f7edee;
  color: #a64d79;
  font-size: 0.75rem;
  border-radius: 9999px;
`

const LastSync = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`

const PendingText = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`

const KnowledgeSourcesSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
`

const KnowledgeSourcesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`

const KnowledgeSourcesTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
`

const AddSourceButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #a64d79;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #8a3d66;
  }
`

const KnowledgeSourcesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const TableHeader = styled.thead`
  border-bottom: 1px solid #e5e7eb;
`

const TableHeaderRow = styled.tr``

const TableHeaderCell = styled.th`
  text-align: left;
  padding: 0.75rem 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const TableBody = styled.tbody``

const TableRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`

const TableCell = styled.td`
  padding: 1rem 0;
  vertical-align: middle;
`

const SourceCell = styled(TableCell)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const SourceIconContainer = styled.div<{ $isInternal?: boolean }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => (props.$isInternal ? '#8B1538' : '#f3f4f6')};
  flex-shrink: 0;
`

const SourceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const SourceName = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
`

const SourceType = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`

const ArticlesCell = styled(TableCell)`
  font-size: 0.875rem;
  color: #111827;
`

const LastSyncedCell = styled(TableCell)`
  font-size: 0.875rem;
  color: #6b7280;
`

const StatusCell = styled(TableCell)`
  text-align: right;
`

const SyncedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  background-color: #dcfce7;
  color: #15803d;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
`

const Overview = React.memo(() => {
  const [isLoading, setIsLoading] = useState(true)
  const [timeRangeFilter, setTimeRangeFilter] = useState('30days')
  const [volumeView, setVolumeView] = useState('all')

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000)
    return () => clearTimeout(timer)
  }, [])

  const SkeletonCard = () => (
    <Card>
      <SkeletonCardContent>
        <SkeletonBar $width='50%' $height='1rem' $marginBottom='1rem' />
        <SkeletonBar $width='75%' $height='2.5rem' $marginBottom='0.5rem' />
        <SkeletonBar $width='33%' $height='1rem' />
      </SkeletonCardContent>
    </Card>
  )

  const channelData = [
    {
      name: 'Email',
      value: 45,
      requests: 1840,
      trend: 12,
      color: '#5C1631',
      icon: Mail,
    },
    {
      name: 'Slack Bot',
      value: 35,
      requests: 1440,
      trend: 24,
      color: '#BFA6CC',
      icon: MessageSquare,
    },
    {
      name: 'Manual Requests',
      value: 20,
      requests: 780,
      trend: -8,
      color: '#C9CCD1',
      icon: UserCheck,
    },
  ]

  const volumeData = [
    { week: 'W1', all: 520, email: 236, slack: 184, manual: 100 },
    { week: 'W2', all: 580, email: 262, slack: 206, manual: 112 },
    { week: 'W3', all: 590, email: 268, slack: 208, manual: 114 },
    { week: 'W4', all: 640, email: 290, slack: 226, manual: 124 },
    { week: 'W5', all: 620, email: 278, slack: 218, manual: 124 },
    { week: 'W6', all: 560, email: 252, slack: 198, manual: 110 },
    { week: 'W7', all: 550, email: 254, slack: 200, manual: 96 },
  ]

  const siteConfigs = [
    {
      name: 'Bay Area',
      status: 'active',
      services: ['Transit', 'Parking', 'Micromobility'],
      lastSync: '2 hours ago',
    },
    {
      name: 'New York',
      status: 'active',
      services: ['Transit', 'Parking'],
      lastSync: '1 hour ago',
    },
    { name: 'Denver', status: 'coming-soon', services: [], lastSync: 'N/A' },
  ]

  const knowledgeSources = [
    {
      name: 'Internal Knowledge Base',
      type: 'Internal',
      articles: 147,
      lastSynced: 'Jan 14, 2026 at 3:42pm',
      status: 'Synced',
      isInternal: true,
    },
    {
      name: 'Ridepanda FAQ',
      type: 'Integration',
      articles: 52,
      lastSynced: 'Jan 14, 2026 at 2:15pm',
      status: 'Synced',
      isInternal: false,
    },
    {
      name: 'Wayleadr FAQ',
      type: 'Integration',
      articles: 38,
      lastSynced: 'Jan 14, 2026 at 2:15pm',
      status: 'Synced',
      isInternal: false,
    },
    {
      name: 'OMNY Help Center',
      type: 'Integration',
      articles: 64,
      lastSynced: 'Jan 14, 2026 at 1:30pm',
      status: 'Synced',
      isInternal: false,
    },
    {
      name: 'ORCA Support Docs',
      type: 'Integration',
      articles: 41,
      lastSynced: 'Jan 14, 2026 at 1:30pm',
      status: 'Synced',
      isInternal: false,
    },
    {
      name: 'BayPass FAQ',
      type: 'Integration',
      articles: 29,
      lastSynced: 'Jan 14, 2026 at 12:00pm',
      status: 'Synced',
      isInternal: false,
    },
  ]

  return (
    <Tooltip.Provider delayDuration={200}>
      <div>
        {/* Main Content */}
        <MainContent>
          <ContentContainer>
            <TitleSection>
              <PageTitle>AI Concierge Overview</PageTitle>
              <PageDescription>
                Monitor performance, response rates, and channel throughput for
                your AI-powered employee concierge.
              </PageDescription>
            </TitleSection>

            {isLoading ? (
              <GridContainer>
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </GridContainer>
            ) : (
              <GridContainer>
                <KPICard $delay='0ms'>
                  <KPILabel>Questions Handled</KPILabel>
                  <KPIValueContainer>
                    <KPIValue>
                      <CountUp end={4060} duration={1.8} separator=',' />
                    </KPIValue>
                  </KPIValueContainer>
                  <ChangeIndicator>
                    <ArrowUp style={{ width: '1rem', height: '1rem' }} />
                    <span>18% from last month</span>
                  </ChangeIndicator>
                </KPICard>

                <KPICard $delay='100ms'>
                  <KPILabel>Auto-Resolved Rate</KPILabel>
                  <KPIValueContainer>
                    <KPIValue>
                      <CountUp end={82} duration={1.8} suffix='%' />
                    </KPIValue>
                  </KPIValueContainer>
                  <ChangeIndicator>
                    <ArrowUp style={{ width: '1rem', height: '1rem' }} />
                    <span>5% from last month</span>
                  </ChangeIndicator>
                </KPICard>

                <KPICard $delay='200ms'>
                  <KPILabel>Avg Response Time</KPILabel>
                  <KPIValueContainer>
                    <KPIValue>
                      <CountUp end={4.3} duration={1.8} decimals={1} />
                    </KPIValue>
                    <KPIUnit>s</KPIUnit>
                  </KPIValueContainer>
                  <ChangeIndicator>
                    <ArrowDown style={{ width: '1rem', height: '1rem' }} />
                    <span>10% from last month</span>
                  </ChangeIndicator>
                </KPICard>

                <KPICard $delay='300ms'>
                  <KPILabel>Human Escalations</KPILabel>
                  <KPIValueContainer>
                    <KPIValue>
                      <CountUp end={730} duration={1.8} />
                    </KPIValue>
                  </KPIValueContainer>
                  <ChangeIndicator>
                    <ArrowDown style={{ width: '1rem', height: '1rem' }} />
                    <span>7% from last month</span>
                  </ChangeIndicator>
                </KPICard>

                <KPICard $delay='400ms'>
                  <KPILabel>Employee CSAT</KPILabel>
                  <KPIValueContainer>
                    <KPIValue>
                      <CountUp end={4.7} duration={1.8} decimals={1} />
                    </KPIValue>
                    <Star
                      style={{
                        width: '1.25rem',
                        height: '1.25rem',
                        color: '#fbbf24',
                        fill: '#fbbf24',
                      }}
                    />
                  </KPIValueContainer>
                  <ChangeIndicator>
                    <ArrowUp style={{ width: '1rem', height: '1rem' }} />
                    <span>0.2 from last month</span>
                  </ChangeIndicator>
                </KPICard>
              </GridContainer>
            )}

            <ChartsGrid>
              <ChartCard
                style={{ animationDelay: isLoading ? '0ms' : '500ms' }}
              >
                <ChartHeader>
                  <ChartTitle>Support Channels Overview</ChartTitle>
                  <FilterButtons>
                    <FilterButton
                      onClick={() => setTimeRangeFilter('7days')}
                      $active={timeRangeFilter === '7days'}
                    >
                      7 days
                    </FilterButton>
                    <FilterButton
                      onClick={() => setTimeRangeFilter('30days')}
                      $active={timeRangeFilter === '30days'}
                    >
                      30 days
                    </FilterButton>
                    <FilterButton
                      onClick={() => setTimeRangeFilter('quarter')}
                      $active={timeRangeFilter === 'quarter'}
                    >
                      Quarter
                    </FilterButton>
                  </FilterButtons>
                </ChartHeader>

                <PieChartGrid>
                  <PieChartContainer>
                    <PieChartWrapper>
                      <RechartsResponsiveContainer width='100%' height='100%'>
                        <RechartsPieChart>
                          <RechartsPie
                            data={channelData}
                            cx='50%'
                            cy='50%'
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey='value'
                            animationBegin={500}
                            animationDuration={1000}
                          >
                            {channelData.map((entry, index) => (
                              <RechartsCell
                                key={`cell-${index}`}
                                fill={entry.color}
                              />
                            ))}
                          </RechartsPie>
                        </RechartsPieChart>
                      </RechartsResponsiveContainer>
                      <PieChartCenter>
                        <PieChartCenterContent>
                          <PieChartValue>
                            <CountUp end={4060} duration={1.8} separator=',' />
                          </PieChartValue>
                          <PieChartLabel>Total</PieChartLabel>
                        </PieChartCenterContent>
                      </PieChartCenter>
                    </PieChartWrapper>
                  </PieChartContainer>

                  <ChannelList>
                    {channelData.map((channel, index) => {
                      const Icon = channel.icon
                      return (
                        <Tooltip.Root key={channel.name}>
                          <Tooltip.Trigger asChild>
                            <ChannelItem>
                              <ChannelInfo>
                                <ChannelIconContainer $color={channel.color}>
                                  <Icon
                                    style={{
                                      width: '1rem',
                                      height: '1rem',
                                      color: channel.color,
                                    }}
                                  />
                                </ChannelIconContainer>
                                <ChannelDetails>
                                  <ChannelName>{channel.name}</ChannelName>
                                  <ChannelRequests>
                                    {!isLoading && (
                                      <CountUp
                                        end={channel.requests}
                                        duration={1.8}
                                        delay={0.5 + index * 0.1}
                                      />
                                    )}{' '}
                                    requests
                                  </ChannelRequests>
                                </ChannelDetails>
                              </ChannelInfo>
                              <ChannelStats>
                                <ChannelPercentage>
                                  {!isLoading && (
                                    <CountUp
                                      end={channel.value}
                                      duration={1.8}
                                      suffix='%'
                                    />
                                  )}
                                </ChannelPercentage>
                                <ChannelTrend $positive={channel.trend > 0}>
                                  {channel.trend > 0 ? (
                                    <ArrowUp
                                      style={{
                                        width: '0.75rem',
                                        height: '0.75rem',
                                      }}
                                    />
                                  ) : (
                                    <ArrowDown
                                      style={{
                                        width: '0.75rem',
                                        height: '0.75rem',
                                      }}
                                    />
                                  )}
                                  {Math.abs(channel.trend)}%
                                </ChannelTrend>
                              </ChannelStats>
                            </ChannelItem>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <TooltipContent sideOffset={5}>
                              {channel.name}: {channel.requests} requests (
                              {channel.value}% of total) with{' '}
                              {channel.trend > 0 ? '+' : ''}
                              {channel.trend}% change from last period
                              <TooltipArrow />
                            </TooltipContent>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      )
                    })}
                  </ChannelList>
                </PieChartGrid>
              </ChartCard>

              <ChartCard
                style={{ animationDelay: isLoading ? '0ms' : '600ms' }}
              >
                <ChartHeader>
                  <ChartTitle>Conversation Volume Over Time</ChartTitle>
                  <FilterButtons>
                    <FilterButton
                      onClick={() => setVolumeView('all')}
                      $active={volumeView === 'all'}
                    >
                      All
                    </FilterButton>
                    <FilterButton
                      onClick={() => setVolumeView('channel')}
                      $active={volumeView === 'channel'}
                    >
                      By Channel
                    </FilterButton>
                  </FilterButtons>
                </ChartHeader>
                <BarChartContainer>
                  <RechartsResponsiveContainer width='100%' height='100%'>
                    <RechartsBarChart data={volumeData}>
                      <RechartsCartesianGrid
                        strokeDasharray='3 3'
                        stroke='#E5E7EB'
                      />
                      <RechartsXAxis
                        dataKey='week'
                        stroke='#6B7280'
                        fontSize={12}
                      />
                      <RechartsYAxis stroke='#6B7280' fontSize={12} />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: '#1F2937',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                        }}
                      />
                      {volumeView === 'all' ? (
                        <RechartsBar
                          dataKey='all'
                          fill='#A64D79'
                          radius={[4, 4, 0, 0]}
                        />
                      ) : (
                        <>
                          <RechartsLegend />
                          <RechartsBar
                            dataKey='email'
                            stackId='a'
                            fill='#5C1631'
                            radius={[0, 0, 0, 0]}
                          />
                          <RechartsBar
                            dataKey='slack'
                            stackId='a'
                            fill='#BFA6CC'
                            radius={[0, 0, 0, 0]}
                          />
                          <RechartsBar
                            dataKey='manual'
                            stackId='a'
                            fill='#C9CCD1'
                            radius={[4, 4, 0, 0]}
                          />
                        </>
                      )}
                    </RechartsBarChart>
                  </RechartsResponsiveContainer>
                </BarChartContainer>
                <BarChartFooter>
                  Track adoption and throughput of AI support (30-day view)
                </BarChartFooter>
              </ChartCard>
            </ChartsGrid>

            <ConfigCard style={{ animationDelay: isLoading ? '0ms' : '700ms' }}>
              <ConfigTitle>Configuration Snapshot</ConfigTitle>
              <ConfigGrid>
                {siteConfigs.map((site, index) => (
                  <SiteCard
                    key={site.name}
                    $disabled={site.status === 'coming-soon'}
                    style={{ animationDelay: `${700 + index * 100}ms` }}
                  >
                    <SiteHeader>
                      <SiteName>{site.name}</SiteName>
                      <StatusBadge $active={site.status === 'active'}>
                        {site.status === 'active' ? 'Active' : 'Coming Soon'}
                      </StatusBadge>
                    </SiteHeader>
                    {site.services.length > 0 ? (
                      <>
                        <ServicesContainer>
                          {site.services.map((service) => (
                            <ServiceTag key={service}>{service}</ServiceTag>
                          ))}
                        </ServicesContainer>
                        <LastSync>Last sync: {site.lastSync}</LastSync>
                      </>
                    ) : (
                      <PendingText>Configuration pending</PendingText>
                    )}
                  </SiteCard>
                ))}
              </ConfigGrid>

              <KnowledgeSourcesSection>
                <KnowledgeSourcesHeader>
                  <KnowledgeSourcesTitle>
                    Knowledge Sources
                  </KnowledgeSourcesTitle>
                  <AddSourceButton>
                    <Plus style={{ width: '1rem', height: '1rem' }} />
                    Add Source
                  </AddSourceButton>
                </KnowledgeSourcesHeader>
                <KnowledgeSourcesTable>
                  <TableHeader>
                    <TableHeaderRow>
                      <TableHeaderCell>SOURCE</TableHeaderCell>
                      <TableHeaderCell>ARTICLES</TableHeaderCell>
                      <TableHeaderCell>LAST SYNCED</TableHeaderCell>
                      <TableHeaderCell style={{ textAlign: 'right' }}>
                        STATUS
                      </TableHeaderCell>
                    </TableHeaderRow>
                  </TableHeader>
                  <TableBody>
                    {knowledgeSources.map((source) => (
                      <TableRow key={source.name}>
                        <SourceCell>
                          <SourceIconContainer $isInternal={source.isInternal}>
                            {source.isInternal ? (
                              <BookOpen
                                style={{
                                  width: '1.25rem',
                                  height: '1.25rem',
                                  color: 'white',
                                }}
                              />
                            ) : (
                              <LinkIcon
                                style={{
                                  width: '1.25rem',
                                  height: '1.25rem',
                                  color: '#6b7280',
                                }}
                              />
                            )}
                          </SourceIconContainer>
                          <SourceInfo>
                            <SourceName>{source.name}</SourceName>
                            <SourceType>{source.type}</SourceType>
                          </SourceInfo>
                        </SourceCell>
                        <ArticlesCell>{source.articles} articles</ArticlesCell>
                        <LastSyncedCell>{source.lastSynced}</LastSyncedCell>
                        <StatusCell>
                          <SyncedBadge>
                            <span>✓</span>
                            {source.status}
                          </SyncedBadge>
                        </StatusCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </KnowledgeSourcesTable>
              </KnowledgeSourcesSection>
            </ConfigCard>
          </ContentContainer>
        </MainContent>
      </div>
    </Tooltip.Provider>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  Overview.displayName = 'Overview'
}

export default Overview
